import React, { useCallback, useMemo } from "react";
import { motion } from "framer-motion";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";

// ICONS
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import StorefrontIcon from "@mui/icons-material/Storefront";

// UTILS
import { cleanSpecialCharacters, parseFloatToFixed } from "../../../extras";
import { cpfCnpjMask, formatFinance } from "../../../utils/formatters";
import { blue, grey, red } from "@mui/material/colors";
import { getChannelLogoPath } from "../../../utils/merchant";

// Animation variants
const itemVariants = {
  hidden: { y: 10, opacity: 0 },
  show: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
  exit: {
    y: -10,
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};

const iconVariants = {
  hover: {
    scale: 1.1,
    transition: { duration: 0.2 },
  },
  tap: {
    scale: 0.95,
    transition: { duration: 0.1 },
  },
};

// Static color palette - no theme dependency
const colors = {
  primary: "#2563eb",
  secondary: "#64748b",
  info: "#3b82f6",
  success: "#10b981",
  error: "#ef4444",
  warning: "#f59e0b",
  textPrimary: "#1e293b",
  textSecondary: "#64748b",
  textDisabled: "#94a3b8",
  background: "#ffffff",
  grey50: "#f8fafc",
  grey100: "#f1f5f9",
  grey200: "#e2e8f0",
  grey300: "#cbd5e1",
  grey400: "#94a3b8",
  grey500: "#64748b",
  grey600: "#475569",
  purple: "#9c27b0",
  purpleDark: "#7b1fa2",
};

// Helper function to create rgba colors
const rgba = (color, alpha) => {
  const hex = color.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export default function FiscalTicketRow({
  id,
  index = null,
  orderIndex,
  orderChannel,
  total,
  type,
  status,
  recipient_identification,
  seeDetails,
  deleteAction,
  disableActions = false,
}) {
  const handleSeeDetails = useCallback(
    (e) => {
      e.stopPropagation();
      seeDetails(id);
    },
    [seeDetails, id]
  );

  const handleDeleteAction = useCallback(
    (e) => {
      e.stopPropagation();
      deleteAction();
    },
    [deleteAction]
  );

  // Memoized styling calculations
  const styling = useMemo(() => {
    const isAuthorized = status === "autorizado";

    return {
      status: {
        bgcolor: isAuthorized
          ? rgba(colors.success, 0.1)
          : rgba(colors.error, 0.1),
        color: isAuthorized ? colors.success : colors.error,
        icon: isAuthorized ? CheckCircleIcon : ErrorIcon,
        iconColor: isAuthorized ? colors.success : colors.error,
      },
      background:
        status !== "autorizado" ? rgba(colors.error, 0.03) : colors.background,
      border:
        status !== "autorizado"
          ? rgba(colors.error, 0.2)
          : rgba(colors.grey300, 0.7),
      hoverBorder:
        status !== "autorizado" ? rgba(colors.error, 0.4) : colors.grey400,
    };
  }, [status]);

  const StatusIcon = styling.status.icon;
  const channelLogoPath = orderChannel
    ? getChannelLogoPath(orderChannel)
    : null;

  return (
    <motion.div
      variants={itemVariants}
      initial="hidden"
      animate="show"
      exit="exit"
      layout
    >
      <Paper
        elevation={0}
        component={motion.div}
        onClick={handleSeeDetails}
        whileHover={{
          y: -2,
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          backgroundColor: rgba(colors.background, 0.9),
        }}
        sx={{
          mb: 1.5,
          borderRadius: 2,
          backgroundColor: styling.background,
          overflow: "hidden",
          border: `1px solid ${styling.border}`,
          position: "relative",
          "&:hover": {
            cursor: "pointer",
            borderColor: styling.hoverBorder,
          },
        }}
      >
        {/* Desktop version */}
        <Box
          sx={{
            px: 2,
            py: 1,
            position: "relative",
            zIndex: 1,
            display: { xs: "none", sm: "block" },
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            {/* Left side - Ticket info */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ flex: 1 }}
            >
              {/* Index Badge */}
              {index && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: rgba(colors.primary, 0.1),
                    color: colors.primary,
                    borderRadius: 1.5,
                    p: 0.75,
                    minWidth: 40,
                    height: 40,
                    fontWeight: 700,
                    fontSize: "1rem",
                  }}
                >
                  {index}
                </Box>
              )}

              {/* Channel Logo */}
              {channelLogoPath && (
                <Tooltip title={`Canal: ${orderChannel}`}>
                  <Avatar
                    src={channelLogoPath}
                    sx={{
                      width: 36,
                      height: 36,
                      border: `2px solid ${rgba(colors.grey300, 0.7)}`,
                    }}
                  >
                    <StorefrontIcon />
                  </Avatar>
                </Tooltip>
              )}

              {/* Type */}
              <Tooltip title="Tipo de Nota Fiscal">
                <Chip
                  label={type === 2 || type === "NF-e" ? "NF-e" : "NFC-e"}
                  size="medium"
                  sx={{
                    height: 28,
                    fontSize: "0.95rem",
                    bgcolor: rgba(colors.grey600, 0.1),
                    color: colors.textPrimary,
                    fontWeight: 500,
                  }}
                />
              </Tooltip>

              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ flex: 1 }}
              >
                {/* Order Index */}
                <Tooltip title="Número do Pedido">
                  <Chip
                    icon={
                      <AssignmentIcon
                        sx={{ fontSize: 16, color: colors.info }}
                      />
                    }
                    label={`Pedido: ${orderIndex}`}
                    size="medium"
                    sx={{
                      height: 28,
                      fontSize: "0.95rem",
                      bgcolor: rgba(colors.info, 0.1),
                      color: colors.info,
                      fontWeight: 600,
                    }}
                  />
                </Tooltip>

                {/* Total Amount */}
                <Tooltip
                  title={`Valor Total: ${formatFinance(
                    parseFloatToFixed(total)
                  )}`}
                >
                  <Chip
                    icon={
                      <AttachMoneyOutlinedIcon
                        sx={{ fontSize: 16, color: colors.success }}
                      />
                    }
                    label={formatFinance(parseFloatToFixed(total))}
                    size="medium"
                    sx={{
                      height: 28,
                      fontSize: "0.95rem",
                      bgcolor: rgba(colors.success, 0.1),
                      color: colors.success,
                      fontWeight: 600,
                    }}
                  />
                </Tooltip>

                {/* Recipient Identification */}
                <Tooltip title="Documento do Destinatário">
                  <Chip
                    icon={
                      <PersonIcon
                        sx={{
                          fontSize: 16,
                          color: recipient_identification
                            ? colors.purpleDark
                            : colors.textSecondary,
                        }}
                      />
                    }
                    label={
                      recipient_identification
                        ? cpfCnpjMask(recipient_identification)
                        : "Sem Documento"
                    }
                    size="medium"
                    sx={{
                      height: 28,
                      fontSize: "0.95rem",
                      bgcolor: recipient_identification
                        ? rgba(colors.purple, 0.1)
                        : rgba(colors.grey500, 0.1),
                      color: recipient_identification
                        ? colors.purpleDark
                        : colors.textSecondary,
                      fontWeight: 500,
                    }}
                  />
                </Tooltip>
              </Stack>
            </Stack>

            {/* Right side - Status and actions */}
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {/* Status Badge */}
              <Tooltip title={`Status: ${cleanSpecialCharacters(status)}`}>
                <Chip
                  icon={
                    <StatusIcon
                      sx={{ fontSize: 16, color: styling.status.iconColor }}
                    />
                  }
                  label={cleanSpecialCharacters(status)}
                  size="medium"
                  sx={{
                    height: 28,
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    bgcolor: styling.status.bgcolor,
                    color: styling.status.color,
                  }}
                />
              </Tooltip>

              {/* Action Buttons */}
              <Box
                sx={{
                  bgcolor: rgba(colors.grey500, 0.1),
                  borderRadius: 1,
                  padding: "4px",
                  display: "flex",
                  gap: 0.5,
                }}
              >
                <Tooltip title="Visualizar Detalhes">
                  <span>
                    <IconButton
                      component={motion.button}
                      variants={iconVariants}
                      whileHover="hover"
                      whileTap="tap"
                      disabled={disableActions}
                      onClick={handleSeeDetails}
                      sx={{
                        p: 0.9,
                        bgcolor: rgba(colors.info, 0.1),
                        color: colors.info,
                        "&:hover": {
                          bgcolor: rgba(colors.info, 0.2),
                        },
                        "&.Mui-disabled": {
                          bgcolor: rgba(colors.grey500, 0.1),
                          color: colors.textDisabled,
                        },
                      }}
                    >
                      <VisibilityIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Cancelar/Excluir">
                  <span>
                    <IconButton
                      component={motion.button}
                      variants={iconVariants}
                      whileHover="hover"
                      whileTap="tap"
                      disabled={disableActions}
                      onClick={handleDeleteAction}
                      sx={{
                        p: 0.9,
                        bgcolor: rgba(colors.error, 0.1),
                        color: colors.error,
                        "&:hover": {
                          bgcolor: rgba(colors.error, 0.2),
                        },
                        "&.Mui-disabled": {
                          bgcolor: rgba(colors.grey500, 0.1),
                          color: colors.textDisabled,
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Stack>
          </Stack>
        </Box>

        {/* Mobile version */}
        <Box
          sx={{
            px: 2,
            py: 1,
            position: "relative",
            zIndex: 1,
            display: { xs: "block", sm: "none" },
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            {/* Left side - Compact info */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ flex: 1 }}
            >
              {/* Index Badge */}
              {index && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: rgba(colors.primary, 0.1),
                    color: colors.primary,
                    borderRadius: 1.5,
                    p: 0.6,
                    minWidth: 36,
                    height: 36,
                    fontWeight: 700,
                  }}
                >
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>
                    {index}
                  </Typography>
                </Box>
              )}

              {/* Channel Logo - Mobile */}
              {channelLogoPath && (
                <Avatar
                  src={channelLogoPath}
                  sx={{
                    width: 28,
                    height: 28,
                    border: `1px solid ${rgba(colors.grey300, 0.7)}`,
                  }}
                >
                  <StorefrontIcon sx={{ fontSize: 14 }} />
                </Avatar>
              )}

              {/* Order Index */}
              <Chip
                label={`#${orderIndex}`}
                size="small"
                sx={{
                  height: 24,
                  fontSize: "0.85rem",
                  bgcolor: rgba(colors.info, 0.1),
                  color: colors.info,
                  fontWeight: 600,
                }}
              />

              {/* Status */}
              <Chip
                icon={
                  <StatusIcon
                    sx={{ fontSize: 14, color: styling.status.iconColor }}
                  />
                }
                label={cleanSpecialCharacters(status)}
                size="small"
                sx={{
                  height: 24,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  bgcolor: styling.status.bgcolor,
                  color: styling.status.color,
                }}
              />
            </Stack>

            {/* Right side - Actions only */}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Tooltip title="Ver">
                <span>
                  <IconButton
                    disabled={disableActions}
                    onClick={handleSeeDetails}
                    size="small"
                    sx={{
                      p: 0.7,
                      bgcolor: rgba(blue[500], 0.1),
                      color: blue[600],
                      "&:hover": {
                        bgcolor: rgba(blue[500], 0.2),
                      },
                      "&.Mui-disabled": {
                        bgcolor: rgba(grey[500], 0.1),
                        color: grey[400],
                      },
                    }}
                  >
                    <VisibilityIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Excluir">
                <span>
                  <IconButton
                    disabled={disableActions}
                    onClick={handleDeleteAction}
                    size="small"
                    sx={{
                      p: 0.7,
                      bgcolor: rgba(red[500], 0.1),
                      color: red[600],
                      "&:hover": {
                        bgcolor: rgba(red[500], 0.2),
                      },
                      "&.Mui-disabled": {
                        bgcolor: rgba(grey[500], 0.1),
                        color: grey[400],
                      },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </motion.div>
  );
}
